import { CATEGORY_KEYS } from "./clientsFilterChips";
import { clientFullName, personName } from "@/utils/personName";

const BLOCKER_KEYS = Object.freeze({
  rto_conflict: "client_duplicates.blockers.rto_conflict",
  currency_conflict: "client_duplicates.blockers.currency_conflict",
  storno_collision: "client_duplicates.blockers.storno_collision",
  dismissed_pair: "client_duplicates.blockers.dismissed_pair",
  tinkoff_in_flight: "client_duplicates.blockers.tinkoff_in_flight",
  demo: "client_duplicates.blockers.demo",
});

export function categoryLabel(value, t) {
  if (value == null || String(value).trim() === "") return "—";
  return Object.prototype.hasOwnProperty.call(CATEGORY_KEYS, value) ? t(CATEGORY_KEYS[value]) : value;
}

const RESULT_FIELD_KEYS = Object.freeze({
  fio: "client_duplicates.fields.fio",
  phone: "client_duplicates.fields.phone",
  email: "client_duplicates.fields.email",
  passport: "client_duplicates.fields.passport",
  driver_license: "client_duplicates.fields.driver_license",
  balance: "client_duplicates.fields.balance",
  category: "client_duplicates.fields.category",
  discounts: "client_duplicates.fields.discounts",
  notes: "client_duplicates.fields.notes",
  blocked_balance: "client_duplicates.fields.blocked_balance",
  debt: "client_duplicates.fields.debt",
  tinkoff_card_bound: "client_duplicates.fields.tinkoff_card_bound",
  problems_description: "client_duplicates.fields.problems_description",
  debt_description: "client_duplicates.fields.debt_description",
});
const MOVED_LABELS = Object.freeze({
  bookings: "client_duplicates.records.bookings",
  counts: "client_duplicates.records.payments",
  tinkoff_payments: "client_duplicates.records.tinkoff_payments",
  stripe_payments: "client_duplicates.records.stripe_payments",
  yandex_pay_payments: "client_duplicates.records.yandex_pay_payments",
  debts: "client_duplicates.records.debts",
  fines: "client_duplicates.records.fines",
  invoices: "client_duplicates.records.invoices",
  deposit_holds: "client_duplicates.records.deposits",
  clients_files: "client_duplicates.records.files",
  driver_documents: "client_duplicates.records.driver_documents",
  signatures: "client_duplicates.records.signatures",
  rent_to_own_contracts: "client_duplicates.records.rent_to_own_contracts",
  leads: "client_duplicates.records.leads",
  client_messages: "client_duplicates.records.messages",
  channel_identities: "client_duplicates.records.messenger_links",
  lead_tasks: "client_duplicates.records.tasks",
  noticed_notifications: "client_duplicates.records.notifications",
  noticed_events: "client_duplicates.records.notification_events",
  client_duplicate_dismissals: "client_duplicates.records.dismissals",
  albums: "client_duplicates.records.albums",
  client_checks: "client_duplicates.records.client_checks",
  messenger_undelivered_messages: "client_duplicates.records.undelivered_messages",
});

function textValue(value) {
  return value == null ? "" : String(value).trim();
}

function numberValue(value) {
  const text = textValue(value);
  return text && Number.isFinite(Number(text)) ? String(Number(text)) : text;
}

export function clientNotesLabel(client, t) {
  const notes = [];
  if (client.problems) notes.push(t("client_duplicates.problem"));
  if (client.debtor) notes.push(t("client_duplicates.debtor"));
  if (client.driver_status === "blacklisted") notes.push(t("client_duplicates.blacklisted"));
  return notes.join(", ") || t("client_duplicates.no_notes");
}

function resultValues(client, t) {
  const bound = client.tinkoff_card_bound === true;
  return {
    ...client,
    // Group cards contain joined documents; preview contains the original columns.
    passport: client.passport == null ? [client.passport_series, client.passport_number].map(textValue).join("") : client.passport,
    driver_license: client.driver_license == null ? [client.driver_series, client.driver_number].map(textValue).join("") : client.driver_license,
    balance: numberValue(client.balance),
    category: categoryLabel(client.category, t),
    blocked_balance: numberValue(client.blocked_balance),
    debt: numberValue(client.debt),
    discounts: [client.sale, client.sale_cash].map(numberValue),
    notes: clientNotesLabel(client, t),
    tinkoff_card_bound: bound
      ? [t("client_duplicates.yes"), textValue(client.tinkoff_card_number)].filter(Boolean).join(" · ")
      : t("client_duplicates.no"),
  };
}

export function buildResultEntries(result, main, t) {
  const values = resultValues(result || {}, t);
  const previous = resultValues(main || {}, t);
  return Object.keys(RESULT_FIELD_KEYS).filter((key) => {
    if (key === "blocked_balance" || key === "debt") return Number(values[key]) !== 0;
    if (key === "problems_description" || key === "debt_description") return textValue(values[key]) !== "";
    return true;
  }).map((key) => ({
    key,
    label: t(RESULT_FIELD_KEYS[key]),
    value: (key === "discounts" ? values[key].filter((value) => value !== "").join(" / ") : textValue(values[key])) || "—",
    changed: key === "discounts"
      ? JSON.stringify(values[key]) !== JSON.stringify(previous[key])
      : textValue(values[key]) !== textValue(previous[key]),
  }));
}

export function buildMovedEntries(moved, t) {
  return Object.entries(moved || {})
    .map(([key, value]) => ({
      key,
      label: t(MOVED_LABELS[key] || "client_duplicates.records.other"),
      value: Number(value),
    }))
    .filter(({ value }) => Number.isFinite(value) && value > 0);
}

function clientIds(group) {
  return ((group && group.clients) || []).map((client) => client.id);
}

function uniqueIds(ids) {
  return [...new Set((ids || []).filter((id) => id !== null && id !== undefined))];
}

function orderedSelectedIds(selection, group) {
  const selected = new Set(uniqueIds(selection && selection.selectedIds));
  return clientIds(group).filter((id) => selected.has(id));
}

export function createSelection(group) {
  const ids = clientIds(group);
  const suggested = group && group.suggested_main_id;
  const mainId = ids.includes(suggested) ? suggested : (ids[0] || null);
  return { mainId, selectedIds: ids };
}

export function changeMain(selection, mainId) {
  return {
    mainId,
    selectedIds: uniqueIds([...(selection.selectedIds || []), mainId]),
  };
}

function buildOperationPayload(selection, group) {
  const selectedIds = orderedSelectedIds(selection, group);
  return {
    main_id: selection.mainId,
    merge_ids: selectedIds.filter((id) => id !== selection.mainId),
  };
}

export function buildPreviewPayload(selection, group) {
  return buildOperationPayload(selection, group);
}

export function buildMergePayload(selection, group) {
  return buildOperationPayload(selection, group);
}

export function buildDismissPayload(group) {
  return { client_ids: clientIds(group) };
}

export function createDismissIntent(group) {
  return {
    groupKey: group.key,
    payload: buildDismissPayload(group),
  };
}

export function canMergeSelection({
  selection,
  group,
  blockers,
  previewPending,
  previewReady,
}) {
  if (!selection || !previewReady || previewPending || (blockers || []).length > 0) return false;
  const selectedIds = orderedSelectedIds(selection, group);
  return selectedIds.includes(selection.mainId) && selectedIds.length > 1;
}

export function blockerText(blocker, t) {
  const code = blocker && blocker.code;
  if (BLOCKER_KEYS[code]) return t(BLOCKER_KEYS[code]);
  return t("client_duplicates.blockers.unknown");
}

export function createMergeIntent(selection, group) {
  const selectedIds = orderedSelectedIds(selection, group);
  const selected = new Set(selectedIds);
  return {
    groupKey: group.key,
    payload: buildMergePayload({ mainId: selection.mainId, selectedIds }, group),
    cardsCount: selectedIds.length,
    bookingsCount: (group.clients || []).reduce(
      (sum, client) => sum + (selected.has(client.id) ? Number(client.bookings_count) || 0 : 0),
      0
    ),
  };
}

export function pageToReloadAfterRemoval({ page, totalGroups, pageSize, remainingOnPage }) {
  if (remainingOnPage > 0 || totalGroups <= 0) return null;
  return Math.min(page, Math.max(1, Math.ceil(totalGroups / pageSize)));
}

export function errorCodeFromResponse(data) {
  if (!data) return null;
  if (data.code) return data.code;
  if (data.error_code) return data.error_code;
  if (typeof data.error === "string") return data.error;
  return data.error && data.error.code ? data.error.code : null;
}

// ФИО карточки как в списке клиентов: из фамилии, имени и отчества; поле fio (пустое у части
// старых клиентов) — только запасной вариант.
export function clientDisplayName(client) {
  if (!client) return "";
  return clientFullName(client) || personName(client.fio);
}

// Имена участников группы для плитки — без повторов, в порядке карточек.
export function groupNames(group) {
  const names = ((group && group.clients) || []).map(clientDisplayName).filter(Boolean);
  return [...new Set(names)].join(", ");
}
